import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge, Btn, BmModal, EmptyState } from '../ForBrands/ui/BrandUI';
import Icon from '../ForBrands/ui/icons';
import { API_BASE_URL } from '../../constants';

// Team requests ("I can't find my team") on the admin Teams page.
// Per request the admin can look the team up in the API and link the
// requester to it, create a CACHE partner team, or dismiss the request.
// Dismiss only closes the request; the requester's profile is not touched.
// Requests for one real team from several people (the same groupKey: name,
// gender, state and sport) show as one row, with "Link all" for a match.

export const REQUESTS_URL = `${API_BASE_URL}/admin/team-requests/`;
export const LINK_ALL_URL = `${REQUESTS_URL}link-all/`;
const PAGE_SIZE = 20;

// The requests grouped by the team they ask for, in list order (newest
// first): [{key, requests}]. A request without a groupKey (an older list)
// groups by its name, gender and state.
export const groupRequests = (requests = []) => {
  const groups = [];
  const byKey = {};
  requests.forEach((req) => {
    const key = req.groupKey || [String(req.teamNameNorm || req.teamName || '').trim().toLowerCase(),
      req.gender || '', req.state || ''].join('|');
    if (!byKey[key]) {
      byKey[key] = { key, requests: [] };
      groups.push(byKey[key]);
    }
    byKey[key].requests.push(req);
  });
  return groups;
};

// One plain sentence for what a "Link all" did.
export const linkAllResultMessage = (data = {}, teamName = 'the team') => {
  const linked = data.linkedCount || 0;
  const total = data.total || linked;
  const notLinked = (data.refused || []).length + (data.failed || []).length;
  const head = `Linked ${linked} of ${total} requester${total === 1 ? '' : 's'} to ${teamName}.`;
  return notLinked
    ? `${head} ${notLinked} could not be linked and ${notLinked === 1 ? 'stays' : 'stay'} pending.`
    : head;
};

export const SOURCE_LABEL = {
  cerebro: 'Cerebro',
  cache_teams: 'CACHE Partner',
  sportsradar: 'NCAA D-I',
  genius: 'Genius football',
};

const sourceLabel = (source) => SOURCE_LABEL[source] || source || '';

const TD = 'px-4 py-2.5 bm-body-m text-bm-ink';

const requesterName = (req) => `${req?.name || ''} ${req?.surname || ''}`.trim() || req?.email || 'The requester';

// What the requester is on today, from the snapshot the list sends
// ({teamId, teamName, league, teamSource, hasPlayerId, teams}).
export const requesterNow = (requester) => {
  if (!requester) return { main: '', extra: '', claimed: false };
  const primaryName = (requester.teamName || '').trim();
  const hasPrimary = !!(primaryName || requester.teamId);
  const src = sourceLabel(requester.teamSource);
  const main = hasPrimary
    ? `Already on ${primaryName || 'a team'}${src ? ` (${src})` : ''}`
    : 'No team yet';
  const others = (Array.isArray(requester.teams) ? requester.teams : [])
    .map((n) => (typeof n === 'string' ? n : n?.teamName || n?.name || '').trim())
    .filter((n) => n && n.toLowerCase() !== primaryName.toLowerCase());
  return {
    main,
    extra: others.length ? `Also on ${others.join(', ')}` : '',
    claimed: !!requester.hasPlayerId,
  };
};

// The resolve body for linking the requester to one API match.
export const linkBodyFor = (match, req = {}) => {
  const partner = match.source === 'cache_teams';
  const [, division] = String(match.leagueKey || '').split('|');
  return {
    action: 'link',
    teamId: String((partner ? (match.cacheTeamId || match.teamId) : match.teamId) || ''),
    leagueKey: match.leagueKey || '',
    league: (partner ? (match.cacheLeague || match.league) : (match.catalogueKey || match.league)) || '',
    teamName: match.name || '',
    leagueLabel: match.leagueLabel || '',
    source: match.source || 'cerebro',
    gender: req.gender || '',
    level: division || '',
  };
};

// One plain sentence for what a link did.
export const linkResultMessage = (data = {}, teamName = 'the team', who = 'The requester') => {
  if (data.alreadyOnTeam) return `${who} is already on ${teamName}. Nothing changed on the profile. The request is closed.`;
  if (data.athleteAttached && data.additional) return `${teamName} was added as another team for ${who}. Their main team stays the same.`;
  if (data.athleteAttached) return `${teamName} is now ${who}'s team.`;
  return `The request is linked to ${teamName}. ${who}'s profile was not changed.`;
};

const serverMessage = (err, fallback) => {
  const data = err?.response?.data || {};
  if (err?.response?.status === 409 && !data.message) return 'This request was already handled.';
  const text = [data.message, data.error, data.detail].find((v) => typeof v === 'string' && v.trim() && !/^[a-z0-9_.-]+$/.test(v));
  return text || fallback;
};

const MatchList = ({ state, req, onLink, busy, groupSize = 1 }) => {
  if (state.loading) return <div className="px-4 py-3 bm-body-s text-bm-muted">Searching the API...</div>;
  if (state.error) return <div className="px-4 py-3 bm-body-s text-bm-critical">{state.error}</div>;
  const matches = state.matches || [];
  const apiNote = state.apiError ? (
    <div className="border-b border-bm-border px-4 py-2 bm-body-s text-bm-critical">
      The Cerebro search did not answer, so only partner teams are shown. Try again before creating a team.
    </div>
  ) : null;
  if (matches.length === 0) {
    return (
      <>
        {apiNote}
        <div className="px-4 py-3 bm-body-s text-bm-muted">
          No team with a similar name in the API. Create a partner team if this team is real.
        </div>
      </>
    );
  }
  const requester = req.requester || {};
  return (
    <>
      {apiNote}
      <ul className="divide-y divide-bm-border">
        {matches.map((m) => {
          const onIt = !!m.requesterOnTeam || (!!requester.teamId
            && [m.teamId, m.cacheTeamId].filter(Boolean).map(String).includes(String(requester.teamId)));
          const where = [m.leagueLabel, [m.city, m.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ');
          return (
            <li key={`${m.source}-${m.teamId}-${m.leagueKey || m.league || ''}`} className="flex items-center justify-between gap-3 px-4 py-2.5">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="bm-label-l text-bm-ink">{m.name}</span>
                  <Badge tone="soft">{sourceLabel(m.source)}</Badge>
                  {m.exact && <Badge tone="outline">Same name</Badge>}
                  {onIt && <Badge tone="dark">Requester is on this team</Badge>}
                </div>
                {where && <div className="truncate bm-body-s text-bm-tertiary">{where}</div>}
              </div>
              <Btn size="sm" onClick={() => onLink(req, m)} disabled={busy}>
                <Icon name="link" size={12} className="mr-1" /> {groupSize > 1 ? `Link all ${groupSize}` : 'Link requester'}
              </Btn>
            </li>
          );
        })}
      </ul>
    </>
  );
};

const TeamRequestsModal = ({ requests = [], authHeaders, onClose, onCreatePartner, onResolved }) => {
  const [page, setPage] = useState(0);
  const [openId, setOpenId] = useState(null);
  const [found, setFound] = useState({});
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  // One row per team: a request of its own, or the requests of several
  // people for the same team.
  const groups = groupRequests(requests);
  const pageCount = Math.max(1, Math.ceil(groups.length / PAGE_SIZE));
  const current = Math.min(page, pageCount - 1);
  const pageRows = groups.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE);
  const rowId = (group) => (group.requests.length === 1 ? group.requests[0].request_id : `group:${group.key}`);

  const findInApi = async (group) => {
    const id = rowId(group);
    const req = group.requests[0];
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id);
    // Cached unless the last search failed, so reopening retries.
    if (found[id] && !found[id].error && !found[id].apiError) return;
    setFound((prev) => ({ ...prev, [id]: { loading: true, matches: [] } }));
    try {
      const res = await axios.get(`${REQUESTS_URL}${req.request_id}/matches/`, authHeaders);
      setFound((prev) => ({ ...prev, [id]: { loading: false, matches: res.data?.matches || [], apiError: !!res.data?.apiError } }));
    } catch (err) {
      setFound((prev) => ({ ...prev, [id]: { loading: false, matches: [], error: serverMessage(err, 'Could not search the API. Try again.') } }));
    }
  };

  const resolve = async (req, body) => {
    const res = await axios.post(`${REQUESTS_URL}${req.request_id}/resolve/`, body, authHeaders);
    return res.data || {};
  };

  const runPending = async () => {
    if (!pending) return;
    const { kind, req, match, group } = pending;
    setBusy(true);
    try {
      if (kind === 'dismiss') {
        await resolve(req, { action: 'dismiss' });
        const msg = `Request for "${req.teamName}" dismissed. ${requesterName(req)}'s profile was not changed.`;
        setNotice(msg);
        toast.success(msg);
      } else if (kind === 'dismissAll') {
        await group.requests.reduce((chain, r) => chain.then(() => resolve(r, { action: 'dismiss' })), Promise.resolve());
        const msg = `${group.requests.length} requests for "${group.requests[0].teamName}" dismissed. No profile was changed.`;
        setNotice(msg);
        toast.success(msg);
      } else if (kind === 'link') {
        const data = await resolve(req, linkBodyFor(match, req));
        const msg = linkResultMessage(data, match.name || 'the team', requesterName(req));
        setNotice(msg);
        if (data.athleteAttached || data.alreadyOnTeam) toast.success(msg);
        else toast.info(msg);
      } else if (kind === 'linkAll') {
        const body = { ...linkBodyFor(match, group.requests[0]), requestIds: group.requests.map((r) => r.request_id) };
        const res = await axios.post(LINK_ALL_URL, body, authHeaders);
        const data = res.data || {};
        const msg = linkAllResultMessage(data, match.name || 'the team');
        setNotice(msg);
        if (data.linkedCount) toast.success(msg);
        else toast.info(msg);
      }
      setOpenId(null);
      onResolved?.();
    } catch (err) {
      const dismissing = kind === 'dismiss' || kind === 'dismissAll';
      toast.error(serverMessage(err, dismissing ? 'Could not dismiss the request.' : 'Could not link the requester.'));
      if (err?.response?.status === 409 || kind === 'dismissAll') onResolved?.();
    } finally {
      setBusy(false);
      setPending(null);
    }
  };

  const confirmCopy = (() => {
    if (!pending) return null;
    if (pending.kind === 'dismissAll') {
      const n = pending.group.requests.length;
      return {
        title: 'Dismiss requests',
        body: `Dismiss the ${n} requests for "${pending.group.requests[0].teamName}"? Only the requests are closed. No requester's profile or teams change.`,
        confirmLabel: `Dismiss ${n}`,
      };
    }
    if (pending.kind === 'linkAll') {
      const m = pending.match;
      const n = pending.group.requests.length;
      const where = [sourceLabel(m.source), m.leagueLabel].filter(Boolean).join(', ');
      return {
        title: 'Link all requesters',
        body: `Link all ${n} requesters to ${m.name}${where ? ` (${where})` : ''}? Each one is linked as a single link is: with no team yet, it becomes their team; with a team already, it is added as another team and their main team stays. It counts as verified, like a team code. A requester the team does not fit stays pending.`,
        confirmLabel: `Link all ${n}`,
      };
    }
    const who = requesterName(pending.req);
    if (pending.kind === 'dismiss') {
      return {
        title: 'Dismiss request',
        body: `Dismiss the request for "${pending.req.teamName}"? Only the request is closed. ${who}'s profile and teams are not changed.`,
        confirmLabel: 'Dismiss',
      };
    }
    const m = pending.match;
    const where = [sourceLabel(m.source), m.leagueLabel].filter(Boolean).join(', ');
    return {
      title: 'Link requester',
      body: `Link ${who} to ${m.name}${where ? ` (${where})` : ''}? With no team yet, it becomes their team. With a team already, it is added as another team and their main team stays. It counts as verified, like a team code. The request is closed.`,
      confirmLabel: 'Link requester',
    };
  })();

  const footer = pending ? (
    <>
      <Btn variant="secondary" onClick={() => setPending(null)} disabled={busy}>Cancel</Btn>
      <Btn onClick={runPending} disabled={busy}>{busy ? 'Working...' : confirmCopy.confirmLabel}</Btn>
    </>
  ) : (
    <div className="flex flex-1 items-center justify-between gap-3">
      <span className="bm-body-s text-bm-muted">
        {groups.length === 0
          ? 'Nothing pending'
          : `Showing ${current * PAGE_SIZE + 1}-${Math.min((current + 1) * PAGE_SIZE, groups.length)} of ${groups.length}`}
      </span>
      <div className="flex items-center gap-2">
        <Btn size="sm" variant="secondary" onClick={() => setPage(Math.max(0, current - 1))} disabled={current === 0}>Previous</Btn>
        <span className="bm-body-s text-bm-muted">Page {current + 1} of {pageCount}</span>
        <Btn size="sm" variant="secondary" onClick={() => setPage(Math.min(pageCount - 1, current + 1))} disabled={current >= pageCount - 1}>Next</Btn>
      </div>
    </div>
  );

  // The squad's age group too (ageGroup: picked beside the name or typed
  // in it), so two squads of one program read apart.
  const details = (req) => [
    req.sportLabel,
    req.tierLabel || req.tier,
    req.ageGroup,
    req.gender ? (req.gender === 'F' ? 'Women/Girls' : 'Men/Boys') : '',
  ].filter(Boolean).join(' · ') || '-';

  const matchesRow = (group, onLink) => {
    const id = rowId(group);
    return (
      <tr className="border-b border-bm-border bg-white">
        <td colSpan={6} className="px-4 pb-3">
          <div className="rounded-[8px] border border-bm-border" data-testid={`matches-${id}`}>
            <MatchList
              state={found[id] || { loading: true }}
              req={group.requests[0]}
              busy={busy}
              groupSize={group.requests.length}
              onLink={onLink}
            />
          </div>
        </td>
      </tr>
    );
  };

  const singleRow = (group) => {
    const req = group.requests[0];
    const now = requesterNow(req.requester);
    const isOpen = openId === req.request_id;
    return (
      <React.Fragment key={req.request_id}>
        <tr className="border-b border-bm-border bg-white transition-colors last:border-0 hover:bg-bm-subtle">
          <td className={TD}>
            <div className="bm-label-l text-bm-ink">{req.teamName}</div>
            {(req.city || req.state) && (
              <div className="bm-body-s text-bm-tertiary">{[req.city, req.state].filter(Boolean).join(', ')}</div>
            )}
          </td>
          <td className={TD}>
            <Badge tone={req.role === 'coach' ? 'outline' : 'soft'}>
              {req.role === 'coach' ? 'Coach' : 'Athlete'}
            </Badge>
          </td>
          <td className={TD}>
            <div>{`${req.name || ''} ${req.surname || ''}`.trim() || '-'}</div>
            <div className="bm-body-s text-bm-tertiary">{req.email}</div>
          </td>
          <td className={TD} data-testid={`requester-now-${req.request_id}`}>
            {now.main ? (
              <>
                <div className={now.main === 'No team yet' ? 'text-bm-muted' : 'text-bm-ink'}>{now.main}</div>
                {now.extra && <div className="bm-body-s text-bm-tertiary">{now.extra}</div>}
                {now.claimed && <div className="bm-body-s text-bm-tertiary">Roster spot claimed</div>}
              </>
            ) : <span className="text-bm-tertiary">-</span>}
          </td>
          <td className={`${TD} text-bm-muted`}>{details(req)}</td>
          <td className={TD}>
            <div className="flex flex-wrap items-center gap-2">
              <Btn size="sm" variant="secondary" onClick={() => findInApi(group)} aria-expanded={isOpen}>
                <Icon name="search" size={12} className="mr-1" /> {isOpen ? 'Hide matches' : 'Find in API'}
              </Btn>
              <Btn
                size="sm"
                onClick={() => onCreatePartner?.(req)}
                title="Creates a new CACHE partner team. Use it only when the team is not in the API."
              >
                <Icon name="plus" size={12} className="mr-1" /> Create partner team
              </Btn>
              <Btn size="sm" variant="ghost" onClick={() => setPending({ kind: 'dismiss', req })}>
                Dismiss
              </Btn>
            </div>
          </td>
        </tr>
        {isOpen && matchesRow(group, (r, m) => setPending({ kind: 'link', req: r, match: m }))}
      </React.Fragment>
    );
  };

  const groupRow = (group) => {
    const id = rowId(group);
    const first = group.requests[0];
    const isOpen = openId === id;
    const roles = [...new Set(group.requests.map((r) => (r.role === 'coach' ? 'Coach' : 'Athlete')))];
    return (
      <React.Fragment key={id}>
        <tr className="border-b border-bm-border bg-white transition-colors last:border-0 hover:bg-bm-subtle">
          <td className={TD}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="bm-label-l text-bm-ink">{first.teamName}</span>
              <Badge tone="brand">{`${group.requests.length} requests`}</Badge>
            </div>
            {(first.city || first.state) && (
              <div className="bm-body-s text-bm-tertiary">{[first.city, first.state].filter(Boolean).join(', ')}</div>
            )}
          </td>
          <td className={TD}>
            <div className="flex flex-wrap gap-1">
              {roles.map((role) => <Badge key={role} tone={role === 'Coach' ? 'outline' : 'soft'}>{role}</Badge>)}
            </div>
          </td>
          <td className={TD}>
            {group.requests.map((r) => (
              <div key={r.request_id} className="mb-1 last:mb-0">
                <div>{requesterName(r)}</div>
                <div className="bm-body-s text-bm-tertiary">{r.email}</div>
              </div>
            ))}
          </td>
          <td className={TD} data-testid={`requester-now-${id}`}>
            {group.requests.map((r) => {
              const now = requesterNow(r.requester);
              return (
                <div key={r.request_id} className={`bm-body-s ${now.main === 'No team yet' || !now.main ? 'text-bm-muted' : 'text-bm-ink'}`}>
                  {now.main || '-'}
                </div>
              );
            })}
          </td>
          <td className={`${TD} text-bm-muted`}>{details(first)}</td>
          <td className={TD}>
            <div className="flex flex-wrap items-center gap-2">
              <Btn size="sm" variant="secondary" onClick={() => findInApi(group)} aria-expanded={isOpen}>
                <Icon name="search" size={12} className="mr-1" /> {isOpen ? 'Hide matches' : 'Find in API'}
              </Btn>
              <Btn
                size="sm"
                onClick={() => onCreatePartner?.(first, group.requests)}
                title="Creates a new CACHE partner team and links every requester to it. Use it only when the team is not in the API."
              >
                <Icon name="plus" size={12} className="mr-1" /> Create partner team
              </Btn>
              <Btn size="sm" variant="ghost" onClick={() => setPending({ kind: 'dismissAll', group })}>
                Dismiss all
              </Btn>
            </div>
          </td>
        </tr>
        {isOpen && matchesRow(group, (_r, m) => setPending({ kind: 'linkAll', group, match: m }))}
      </React.Fragment>
    );
  };

  return (
    <BmModal
      title={pending ? confirmCopy.title : (
        <span className="flex items-center gap-2">
          Team requests
          {requests.length > 0 && <Badge tone="brand">{requests.length}</Badge>}
        </span>
      )}
      onClose={() => (busy ? null : pending ? setPending(null) : onClose?.())}
      width={1120}
      footer={footer}
    >
      {pending ? (
        <p className="bm-body-m text-bm-ink">{confirmCopy.body}</p>
      ) : (
        <>
          <p className="mb-4 bm-body-s text-bm-muted">
            Athletes and coaches who could not find their team in onboarding. Try Find in API first: if the team is there, link the requester to it instead of creating a partner team. Requests for the same team from several people share one row.
          </p>
          {notice && (
            <div role="status" className="mb-4 rounded-[8px] border border-bm-border bg-bm-subtle px-4 py-3 bm-body-s text-bm-ink">
              {notice}
            </div>
          )}
          <div className="max-h-[60vh] overflow-y-auto">
            {requests.length === 0 ? (
              <EmptyState>No pending team requests.</EmptyState>
            ) : (
              <div className="overflow-x-auto rounded-[8px] border border-bm-border">
                <table className="w-full text-left">
                  <thead className="bg-bm-subtle">
                    <tr>
                      {['Team', 'Role', 'Requested by', 'Requester now', 'Details', 'Actions'].map((h) => (
                        <th key={h} className="whitespace-nowrap border-b border-bm-border px-4 py-2.5 text-left bm-label-xs uppercase tracking-[0.06em] text-bm-muted">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((group) => (group.requests.length === 1 ? singleRow(group) : groupRow(group)))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </BmModal>
  );
};

export default TeamRequestsModal;
